import type { ContextCostCenter } from '@/types/auth.types';

/**
 * Which cost centres belong to a location (ML-02).
 *
 * A cost centre is tagged to exactly one location, and one that is not tagged is not offered
 * anywhere. Both halves matter:
 *
 *   - **Only this location's.** Picking a cost centre that belongs to another branch is how a
 *     posting ends up attributed to the wrong part of the business, and it is not something the
 *     screen can detect afterwards.
 *   - **Untagged are hidden.** A cost centre with no location is not "everywhere", it is
 *     unconfigured. Offering it would let the same row be posted against from every branch, which
 *     is the situation the tagging exists to end.
 *
 * With no location chosen — a screen showing "All Locations" — every tagged cost centre is
 * offered, because the operator has not yet narrowed to a branch. Untagged ones stay hidden even
 * then, since nothing makes them valid.
 *
 * One function rather than the predicate repeated per screen: the login step, the context
 * switcher and every transaction form have to agree about this, and they only will if they ask the
 * same question.
 */
export function costCentresForLocation<T extends Pick<ContextCostCenter, 'locationId'>>(
  costCentres: readonly T[],
  locationId: string | null | undefined,
): T[] {
  return costCentres.filter(cc =>
    !!cc.locationId && (!locationId || cc.locationId === locationId));
}

/**
 * The cost centre a screen should land on for a location: the one marked default when it is in
 * scope, otherwise the first, otherwise none.
 *
 * Returned rather than assigned so callers keep control of their own state, and so a location with
 * no cost centre at all resolves to null instead of leaving a stale id from the previous location
 * selected.
 */
export function defaultCostCentreFor<T extends Pick<ContextCostCenter, 'id' | 'locationId'> & { isDefault?: boolean }>(
  costCentres: readonly T[],
  locationId: string | null | undefined,
): T | null {
  const scoped = costCentresForLocation(costCentres, locationId);
  return scoped.find(cc => cc.isDefault) ?? scoped[0] ?? null;
}
